import type {
  Message,
  MessageType,
  ToolCall,
  RichContent,
  ChatAction,
  ChatWidgetProps,
  RetryPolicy,
  SessionConfig,
  MessageResult,
} from '../types';
import { MESSAGE_TYPES } from '../constants/messageTypes';
import { parseSSEData } from './sseParser';
import { fetchWithRetry } from '../utils/retryFetch';
import { withRequestTimeout } from '../utils/fetchWithTimeout';
import { resolveEndpoint, getNextMessageId } from '../utils/chatHelpers';
import { navigateToUrl } from '../utils/navigation';
import { threadCapabilityHeaders } from '../utils/threadCapability';
import { detectChatAnalyticsIntent } from '../utils/analyticsIntent';
import { notifyHost } from '../utils/notifyHost';
import { attachmentMetadata } from './chatRequest';

export interface AttachmentPayload {
  name: string;
  type: string;
  size: number;
  dataUrl?: string;
}

export type { MessageResult } from '../types';

export interface StreamingMessageParams {
  // Message content
  text: string;
  attachments?: AttachmentPayload[];
  subject?: string;
  tid?: string;
  sender?: 'user' | 'ai';
  meta?: Record<string, unknown>;
  /** Id for the local echo bubble so the caller can mark it failed later. */
  localMessageId?: string;

  // Endpoint config
  streamEndpoint: string | null | undefined;
  messageCreateEndpoint: string | null | undefined;
  messageEndpoint: string | null | undefined;
  apiBaseUrl: string | null | undefined;
  threadId: string | null;
  threadBase: string | null;

  // Dependencies
  dispatch: (action: ChatAction) => void;
  mountedRef: { current: boolean };
  abortControllerRef: { current: AbortController | null };
  activeRequestIdRef: { current: string | null };
  withAuthHeaders: (h?: Record<string, string>) => Record<string, string>;
  buildPayload: (args: {
    text: string;
    attachments?: AttachmentPayload[];
    subject?: string;
    tid?: string;
    meta?: Record<string, unknown>;
  }) => Record<string, unknown>;
  buildMessagePayload?: ChatWidgetProps['buildMessagePayload'];
  handleJsonResponse: (
    data: Record<string, unknown>,
    localMsg: Message,
    atts: AttachmentPayload[],
    meta?: Record<string, unknown>,
    isActive?: () => boolean
  ) => Promise<MessageResult>;
  onMessageCreate?: (req: Message, res: Message) => void;
  /** Leading `thread` SSE event: capability for a thread the server created implicitly. */
  onThreadIssued?: (issued: { threadId: string; threadToken?: string }) => void;
  trackAnalytics: (action: string, data: Record<string, unknown>) => void;
  buildAnalyticsEnvelope?: (data?: Record<string, unknown>) => Record<string, unknown>;
  trackOutcome?: (outcome: unknown) => void;
  retryPolicy?: RetryPolicy;
  /** Selects the storage the thread capability token lives in (`storage: 'session'`). */
  sessionConfig?: SessionConfig;

  // Feature flags
  enableToolVisualization: boolean;
  enableSourceCitations: boolean;
  enableThinking: boolean;
  enableRichMessages: boolean;
}

type FrameHandle =
  | { kind: 'raf'; id: number }
  | { kind: 'timeout'; id: ReturnType<typeof setTimeout> };

/** Fallback cadence when requestAnimationFrame is unavailable or the tab is hidden. */
const FALLBACK_FRAME_MS = 16;

/**
 * Schedule `cb` for the next animation frame. Falls back to a 16ms timer when rAF is
 * missing (SSR/tests) or the document is hidden (background tabs never run rAF).
 */
function scheduleFrame(cb: () => void): FrameHandle {
  const hidden = typeof document !== 'undefined' && document.hidden === true;
  if (!hidden && typeof requestAnimationFrame === 'function') {
    return { kind: 'raf', id: requestAnimationFrame(() => cb()) };
  }
  return { kind: 'timeout', id: setTimeout(cb, FALLBACK_FRAME_MS) };
}

function cancelFrame(handle: FrameHandle): void {
  if (handle.kind === 'raf') {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(handle.id);
  } else {
    clearTimeout(handle.id);
  }
}

export async function createStreamingMessageFn(
  params: StreamingMessageParams
): Promise<MessageResult> {
  const {
    text,
    attachments: atts = [],
    subject: subj,
    tid,
    sender = 'user',
    meta,
    localMessageId,
    streamEndpoint,
    messageCreateEndpoint,
    messageEndpoint,
    apiBaseUrl,
    threadId,
    threadBase,
    dispatch,
    mountedRef,
    abortControllerRef,
    activeRequestIdRef,
    withAuthHeaders,
    buildPayload,
    buildMessagePayload,
    handleJsonResponse,
    onMessageCreate,
    onThreadIssued,
    trackAnalytics,
    buildAnalyticsEnvelope,
    trackOutcome,
    retryPolicy,
    sessionConfig,
    enableToolVisualization,
    enableSourceCitations,
    enableThinking,
    enableRichMessages,
  } = params;

  const endpoint = resolveEndpoint({
    primary: streamEndpoint,
    messageCreate: messageCreateEndpoint,
    message: messageEndpoint,
    threadBase,
    tid: tid || threadId,
    apiBaseUrl,
  });
  if (!endpoint) throw new Error('No message endpoint configured');

  const requestId = `${getNextMessageId()}-ai`;
  activeRequestIdRef.current = requestId;
  abortControllerRef.current?.abort();
  dispatch({ type: 'CLEAR_STREAMING' });
  const isActiveRequest = () => mountedRef.current && activeRequestIdRef.current === requestId;

  const now = new Date();
  const localId = localMessageId ?? getNextMessageId();
  const localMsg: Message = {
    id: localId,
    sender,
    text,
    timestamp: now.toISOString(),
    status: 'sent',
    ...(atts.length ? { attachments: attachmentMetadata(atts, localId) } : {}),
  };
  if (!(meta && typeof meta === 'object' && meta.silent === true)) {
    dispatch({ type: 'ADD_MESSAGE', message: localMsg });
  }

  const payload = {
    ...buildPayload({ text, attachments: atts, subject: subj, tid, meta }),
    requestId,
    clientMessageId: meta?.clientMessageId || localMsg.id,
    stream: true,
  };

  const bodyPayload =
    typeof buildMessagePayload === 'function' ? buildMessagePayload(payload, localMsg) : payload;

  // Accumulators live outside the try so a user-initiated Stop can keep what
  // already streamed in.
  let accumulatedText = '';
  const toolCalls: ToolCall[] = [];
  const sources: { id: string; title: string; url: string; snippet?: string }[] = [];
  let thinking = '';
  let richContent: RichContent | undefined = undefined;
  let richType: MessageType | null = null;
  // Stored AI message id from the `message_id` event (server contract), if sent.
  let serverMessageId: string | undefined;
  const streamedMessageId = requestId;

  // Text deltas are coalesced: `accumulatedText` is always exact, but the UPDATE_STREAMING
  // dispatch (which re-renders the whole widget) happens at most once per animation frame.
  // Any non-text event, completion, abort or error flushes pending text first so ordering
  // is preserved.
  let pendingTextUpdate = false;
  let scheduledFrame: FrameHandle | null = null;
  const cancelScheduledFrame = () => {
    if (scheduledFrame) {
      cancelFrame(scheduledFrame);
      scheduledFrame = null;
    }
  };
  const flushStreamingText = () => {
    cancelScheduledFrame();
    if (!pendingTextUpdate) return;
    pendingTextUpdate = false;
    if (isActiveRequest()) {
      dispatch({ type: 'UPDATE_STREAMING', text: accumulatedText, requestId });
    }
  };
  const queueStreamingText = () => {
    pendingTextUpdate = true;
    if (scheduledFrame) return;
    scheduledFrame = scheduleFrame(() => {
      scheduledFrame = null;
      flushStreamingText();
    });
  };

  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  let readerFinished = false;
  let requestTimer: ReturnType<typeof setTimeout> | undefined;
  let completed = false;
  const controller = new AbortController();
  const canContinue = () => isActiveRequest() && !controller.signal.aborted;
  const cancelled = (): MessageResult => ({ request: localMsg, aborted: true });
  const requestStartedAt = Date.now();
  const finishJsonResponse = async (data: Record<string, unknown>) => {
    if (!canContinue()) return cancelled();
    const result = await handleJsonResponse(
      data,
      localMsg,
      atts,
      { ...meta, analyticsRequestStartedAt: requestStartedAt },
      canContinue
    );
    completed = true;
    return canContinue() ? result : cancelled();
  };
  try {
    if (!canContinue()) return cancelled();
    // Each request owns its controller and can only update its own turn.
    abortControllerRef.current = controller;
    requestTimer = setTimeout(
      () => controller.abort(new DOMException('Reply exceeded five minutes', 'TimeoutError')),
      300_000
    );
    const readBody = <T>(operation: () => Promise<T>) =>
      withRequestTimeout(operation, { signal: controller.signal, timeoutMs: 60_000 });

    const res = await fetchWithRetry(
      endpoint,
      {
        method: 'POST',
        // Keep a conversation on the same backend replica when the widget API
        // is hosted on a different origin and ingress uses cookie affinity.
        credentials: 'include',
        headers: {
          ...withAuthHeaders(),
          ...threadCapabilityHeaders(tid || threadId, sessionConfig),
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(bodyPayload),
        signal: controller.signal,
        timeoutMs: 60_000,
      },
      retryPolicy,
      {
        onRetry: metadata => {
          trackAnalytics(
            'message_retry',
            buildAnalyticsEnvelope
              ? buildAnalyticsEnvelope({
                  attempt: metadata.attempt,
                  reason: metadata.reason,
                  status: metadata.status,
                  streaming: true,
                })
              : {
                  attempt: metadata.attempt,
                  reason: metadata.reason,
                  status: metadata.status,
                  streaming: true,
                }
          );
        },
      }
    );

    if (!res.ok) {
      let serverError = '';
      try {
        const json = (await readBody(() => res.json())) as Record<string, unknown>;
        if (typeof json?.error === 'string') {
          serverError = json.error;
        } else if (typeof json?.message === 'string') {
          serverError = json.message;
        }
      } catch {
        // Keep generic status code message if body is not JSON.
      }
      throw new Error(`Streaming failed: ${res.status}${serverError ? ` (${serverError})` : ''}`);
    }

    const contentType =
      typeof res.headers?.get === 'function' ? res.headers.get('content-type') || '' : '';
    const normalizedType = contentType.toLowerCase();
    const isJsonResponse =
      normalizedType.includes('application/json') ||
      normalizedType.includes('application/problem+json');

    if (isJsonResponse) {
      const data = await readBody(() => res.json());
      return await finishJsonResponse(data as Record<string, unknown>);
    }

    if (!res.body) {
      let data: unknown;
      try {
        data = await readBody(() => res.json());
      } catch {
        if (controller.signal.aborted) throw controller.signal.reason;
        throw new Error('Response body is not readable');
      }
      return await finishJsonResponse(data as Record<string, unknown>);
    }

    reader = res.body.getReader();
    const readChunk = async () => {
      const chunk = await readBody(() => reader!.read());
      if (chunk.done) readerFinished = true;
      return chunk;
    };
    const decoder = new TextDecoder();

    let buffer = '';
    let dataLines: string[] = [];
    let streamDone = false;

    const flushDataLines = () => {
      if (!canContinue() || dataLines.length === 0) return;
      const data = dataLines.join('\n').trim();
      dataLines = [];
      if (data === '[DONE]') {
        streamDone = true;
        return;
      }
      const event = parseSSEData(data, {
        enableToolVisualization,
        enableSourceCitations,
        enableThinking,
        enableRichMessages,
      });
      if (!event) return;
      if (event.type !== 'text_delta' && event.type !== 'text_replace') {
        // Keep ordering: the UI must see the text that preceded this event first.
        flushStreamingText();
      }
      if (!canContinue()) return;
      switch (event.type) {
        case 'error':
          throw new Error(event.error);
        case 'thread':
          onThreadIssued?.({ threadId: event.threadId, threadToken: event.threadToken });
          break;
        case 'message_id':
          serverMessageId = event.messageId;
          break;
        case 'text_delta':
          accumulatedText += event.text;
          queueStreamingText();
          break;
        case 'text_replace':
          // The server's final text differs from what streamed (a later rewrite changed an
          // earlier sentence): show the final text.
          accumulatedText = event.text;
          queueStreamingText();
          break;
        case 'outcome':
          trackOutcome?.(event.outcome);
          break;
        case 'tool_call':
          toolCalls.push(event.toolCall);
          trackAnalytics(
            'tool_call',
            buildAnalyticsEnvelope
              ? buildAnalyticsEnvelope({
                  threadId: tid || threadId || undefined,
                  toolName: event.toolCall.name,
                  toolCallId: event.toolCall.id,
                  status: event.toolCall.status || 'started',
                  streamed: true,
                })
              : {
                  toolName: event.toolCall.name,
                  toolCallId: event.toolCall.id,
                  status: event.toolCall.status || 'started',
                  streamed: true,
                }
          );
          if (canContinue()) dispatch({ type: 'ADD_TOOL_CALL', toolCall: event.toolCall });
          break;
        case 'tool_result': {
          const idx = toolCalls.findIndex(t => t.id === event.id);
          if (idx >= 0) {
            toolCalls[idx].result = event.result;
            toolCalls[idx].status = 'complete';
            if (canContinue()) dispatch({ type: 'UPDATE_TOOL_CALLS', toolCalls: [...toolCalls] });
          }
          break;
        }
        case 'citation':
          sources.push(event.source);
          break;
        case 'thinking':
          thinking += event.text;
          break;
        case 'rich_content':
          richContent = event.content as RichContent;
          richType = event.messageType as MessageType;
          break;
        case 'escalated':
          if (canContinue()) dispatch({ type: 'SET_ESCALATED', isEscalated: true });
          trackAnalytics(
            'handoff_request',
            buildAnalyticsEnvelope
              ? buildAnalyticsEnvelope({
                  threadId: tid || threadId || undefined,
                  streamed: true,
                  source: 'stream',
                })
              : { streamed: true, source: 'stream' }
          );
          break;
        case 'redirect':
          navigateToUrl(event.url, event.target);
          break;
      }
    };

    const processLine = (line: string) => {
      if (streamDone || !canContinue()) return;
      if (line === '') {
        flushDataLines();
        return;
      }
      if (line.startsWith('data:')) {
        let linePayload = line.slice(5);
        if (linePayload.startsWith(' ')) linePayload = linePayload.slice(1);
        dataLines.push(linePayload);
      }
    };

    const processBuffer = () => {
      const lines = buffer.split(/\r?\n/);
      buffer = lines.pop() ?? '';
      for (const line of lines) {
        processLine(line);
        if (streamDone || !canContinue()) break;
      }
    };

    if (!normalizedType.includes('text/event-stream')) {
      const MAX_JSON_FALLBACK_BYTES = 64_000;
      const checkFallbackSize = (bytes: number) => {
        if (bytes > MAX_JSON_FALLBACK_BYTES) throw new Error('JSON fallback exceeds 64000 bytes');
      };
      // Empty chunks, leading whitespace and a split UTF-8 BOM do not identify the format.
      // Wait for the first meaningful character without buffering an unlimited prefix.
      let firstText = '';
      let fallbackBytes = 0;
      while (!firstText.trimStart() && !readerFinished) {
        if (!canContinue()) return cancelled();
        const first = await readChunk();
        if (first.done) {
          firstText += decoder.decode();
          break;
        }
        fallbackBytes += first.value.byteLength;
        firstText += decoder.decode(first.value, { stream: true });
        if (!firstText.trimStart()) checkFallbackSize(fallbackBytes);
      }
      const trimmed = firstText.trimStart();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        checkFallbackSize(fallbackBytes);
        let jsonText = firstText;
        while (!readerFinished) {
          if (!canContinue()) return cancelled();
          const next = await readChunk();
          if (next.done) {
            jsonText += decoder.decode();
            break;
          }
          fallbackBytes += next.value.byteLength;
          checkFallbackSize(fallbackBytes);
          jsonText += decoder.decode(next.value, { stream: true });
        }
        let data: Record<string, unknown>;
        try {
          data = JSON.parse(jsonText.trim());
        } catch {
          throw new Error('Response body is not valid JSON');
        }
        return await finishJsonResponse(data);
      }
      buffer += firstText;
    }

    if (controller.signal.aborted) throw controller.signal.reason;
    if (!isActiveRequest()) return cancelled();
    dispatch({
      type: 'SET_STREAMING',
      message: {
        id: streamedMessageId,
        sender: 'ai',
        text: '',
        timestamp: new Date().toISOString(),
        status: 'streaming',
        type: MESSAGE_TYPES.TEXT,
      },
    });

    if (buffer) processBuffer();

    while (reader && canContinue() && !streamDone) {
      const readResult = await readChunk();
      if (readResult.done || !isActiveRequest()) break;

      buffer += decoder.decode(readResult.value, { stream: true });
      processBuffer();
    }

    if (controller.signal.aborted) throw controller.signal.reason;
    if (!canContinue()) return cancelled();

    if (!streamDone) {
      if (buffer) processLine(buffer);
      flushDataLines();
    }

    flushStreamingText();

    const finalMessage: Message = {
      id: streamedMessageId,
      ...(serverMessageId ? { serverId: serverMessageId } : {}),
      sender: 'ai',
      text: accumulatedText,
      timestamp: new Date().toISOString(),
      status: 'delivered',
      type: richType ?? MESSAGE_TYPES.TEXT,
      toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
      sources: sources.length > 0 ? sources : undefined,
      thinking: thinking || undefined,
      richContent: richContent || undefined,
    };

    if (controller.signal.aborted) throw controller.signal.reason;
    if (!canContinue()) return cancelled();

    completed = true;
    dispatch({ type: 'CLEAR_STREAMING', requestId });
    dispatch({ type: 'ADD_MESSAGE', message: finalMessage });
    if (!canContinue()) return cancelled();
    if (
      finalMessage.type === MESSAGE_TYPES.PRODUCT_CARDS &&
      finalMessage.richContent?.products?.length
    ) {
      trackAnalytics('product_recommendations', {
        ...(buildAnalyticsEnvelope ? buildAnalyticsEnvelope() : {}),
        count: finalMessage.richContent.products.length,
        ids: finalMessage.richContent.products.map(p => p?.id).filter(Boolean),
      });
    }
    if (!canContinue()) return cancelled();
    notifyHost(onMessageCreate, localMsg, finalMessage);
    if (!canContinue()) return cancelled();
    trackAnalytics(
      'message_sent',
      buildAnalyticsEnvelope
        ? buildAnalyticsEnvelope({
            threadId: tid || threadId || undefined,
            messageId: localMsg.id,
            messageType: localMsg.type || MESSAGE_TYPES.TEXT,
            sender: localMsg.sender,
            characterCount: localMsg.text.length,
            intent: detectChatAnalyticsIntent(localMsg.text),
            hasAttachments: atts?.length > 0,
            attachmentCount: atts?.length || 0,
            streamed: true,
            hadToolCalls: toolCalls.length > 0,
            responseTime: Date.now() - requestStartedAt,
            silent: meta?.silent === true,
          })
        : {
            hasAttachments: atts?.length > 0,
            streamed: true,
            hadToolCalls: toolCalls.length > 0,
          }
    );

    return canContinue()
      ? { request: localMsg, response: finalMessage, streamed: true }
      : cancelled();
  } catch (err) {
    // Show whatever text was pending before the stream is torn down (no-op when inactive).
    flushStreamingText();
    if (err instanceof DOMException && err.name === 'AbortError') {
      // Still the active request and still mounted means the visitor pressed Stop.
      // A supersede (newer send) or unmount clears the active id first, and the idle
      // timeout throws a plain Error, so neither reaches this branch as "user stop".
      const stoppedByUser = isActiveRequest();
      if (activeRequestIdRef.current === requestId) {
        dispatch({ type: 'CLEAR_STREAMING', requestId });
        activeRequestIdRef.current = null;
      }
      if (stoppedByUser && accumulatedText) {
        const partialMessage: Message = {
          id: streamedMessageId,
          ...(serverMessageId ? { serverId: serverMessageId } : {}),
          sender: 'ai',
          text: accumulatedText,
          timestamp: new Date().toISOString(),
          status: 'delivered',
          type: richType ?? MESSAGE_TYPES.TEXT,
          toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
          sources: sources.length > 0 ? sources : undefined,
          thinking: thinking || undefined,
          richContent: richContent || undefined,
          interrupted: true,
        };
        dispatch({ type: 'ADD_MESSAGE', message: partialMessage });
        return { request: localMsg, response: partialMessage, aborted: true };
      }
      return {
        request: localMsg,
        aborted: true,
      };
    }
    // The connection dropped or went idle after part of the reply arrived: keep what the
    // shopper already saw as an interrupted reply instead of replacing it with a send failure.
    const keepPartial = isActiveRequest() && (accumulatedText.trim() || richContent);
    if (activeRequestIdRef.current === requestId) {
      dispatch({ type: 'CLEAR_STREAMING', requestId });
      activeRequestIdRef.current = null;
    }
    if (keepPartial) {
      const partialMessage: Message = {
        id: streamedMessageId,
        ...(serverMessageId ? { serverId: serverMessageId } : {}),
        sender: 'ai',
        text: accumulatedText,
        timestamp: new Date().toISOString(),
        status: 'delivered',
        type: richType ?? MESSAGE_TYPES.TEXT,
        toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
        sources: sources.length > 0 ? sources : undefined,
        thinking: thinking || undefined,
        richContent: richContent || undefined,
        interrupted: true,
      };
      dispatch({ type: 'ADD_MESSAGE', message: partialMessage });
      return { request: localMsg, response: partialMessage };
    }
    throw err;
  } finally {
    if (requestTimer) clearTimeout(requestTimer);
    if (!completed) controller.abort();
    if (reader && !readerFinished) {
      // DONE can precede transport EOF. Close unread data even on success; a broken
      // reader's cancellation promise must never hold up completion or recovery.
      try {
        void reader.cancel().catch(() => {});
      } catch {
        /* Already released. */
      }
    }
    try {
      reader?.releaseLock();
    } catch {
      /* Custom readers may already be released or still settling an aborted read. */
    }
    if (activeRequestIdRef.current === requestId) activeRequestIdRef.current = null;
    // Never leave a frame scheduled past the request's lifetime (completion/abort/unmount).
    cancelScheduledFrame();
  }
}
